/**
 * Order Details API
 * GraphQL queries and types for fetching comprehensive order details
 */

import { graphqlQuery } from '@/services/graphql'

// ============================================================================
// Types
// ============================================================================

export interface QuestionOption {
  id: string
  value: string
  labelEn: string
  labelAr: string
  order: number
  score: number | null
}

export interface Question {
  id: string
  code: string
  questionType: string
  textAr?: string | null
  textEn?: string | null
  options: QuestionOption[]
}

export interface TemplateQuestion {
  id: string
  order: number
  question: Question
}

export interface Survey {
  id: string
  title: string
  description: string | null
  templateQuestions: TemplateQuestion[]
}

export interface SurveyAssignment {
  id: string
  surveyId: string
  surveyTitle: string
  isRequired: boolean
  isActive: boolean
  survey: Survey
}

export interface PendingSurvey {
  id: string
  surveyId: string
  surveyTitle: string
  isRequired: boolean
  isActive: boolean
  survey: Survey
}

export interface SurveySnapshotSurvey {
  id: string
  title: string
  titleEn: string
  titleAr: string
}

export interface SurveySnapshot {
  id: string
  snapshotData: unknown
  createdAt: string
  survey: SurveySnapshotSurvey
}

/**
 * Provider-side identity for a survey respondent. Present only when the respondent
 * holds a provider profile — client/beneficiary respondents come back with null.
 */
export interface RespondentProviderProfile {
  fullName: string | null
  organizationName: string | null
  /** Backend-formatted summary, e.g. "4.5/5 (23 reviews)". English-only, so it is a fallback. */
  ratingDisplay: string | null
  totalReviews: number | null
  /** Decimal field — may arrive as a string, so coerce before formatting. */
  averageRating: number | string | null
}

export interface SurveyRespondent {
  id: string
  firstName: string
  lastName: string
  phoneNumber: string
  providerProfile: RespondentProviderProfile | null
}

export interface ClientAnswer {
  questionText: string
  answer: string
  questionType: string
  questionCode: string
  rawAnswer: unknown
  instanceNumber: number | null
  instanceLabel: string | null
  scope: string
  templateQuestionId: string
  questionBankId: string | null
}

export interface SurveyResponse {
  id: string
  submitted: boolean
  submittedAt: string | null
  answers: unknown
  completionPercentage: number
  surveySnapshot: SurveySnapshot | null
  respondent: SurveyRespondent | null
}

export interface ServiceTemplate {
  id: string
  name: string
  description: string | null
  nameEn?: string | null
  nameAr?: string | null
  surveyAssignments: SurveyAssignment[]
}

export interface Service {
  id: string
  status: string
  serviceName: string
  serviceDescription: string | null
  serviceCategory: string
  basePrice: string
  currency: string
  providerName: string | null
  providerPhone: string | null
  assignedProviderId: string | null
  appointmentStatus: string | null
  appointmentScheduledStart: string | null
  appointmentScheduledEnd: string | null
  locationAddress: string | null
  locationCity: string | null
  locationGoogleMapsLink: string | null
  canCancel: boolean
  isOverdue: boolean
  completedAt: string | null
  createdAt: string
  clientFeedback: string | null
  providerFeedback: string | null
  hasClientFeedback: boolean
  hasProviderFeedback: boolean
  hasPendingSurvey: boolean
  pendingSurveyCount: number
  pendingSurveys: PendingSurvey[]
  clientAnswersWithQuestions: ClientAnswer[]
  clientSurveyResponses: SurveyResponse[]
  serviceTemplate: ServiceTemplate | null
}

/** Client-side overdue when API omits `isOverdue` (avoids backend resolver comparing None to datetime). */
export function computeServiceOverdue(
  service: Pick<Service, 'status' | 'completedAt' | 'appointmentScheduledEnd' | 'appointmentScheduledStart'>
): boolean {
  const status = (service.status || '').toLowerCase()
  if (status === 'completed' || status === 'cancelled') return false
  if (service.completedAt) return false

  const endMs = service.appointmentScheduledEnd ? Date.parse(service.appointmentScheduledEnd) : NaN
  const startMs = service.appointmentScheduledStart ? Date.parse(service.appointmentScheduledStart) : NaN
  const now = Date.now()

  if (!Number.isNaN(endMs)) return now > endMs
  if (!Number.isNaN(startMs)) return now > startMs
  return false
}

export interface AppointmentService {
  id: string
  serviceName: string
  status: string
  providerName: string | null
  completedAt: string | null
}

export interface Appointment {
  id: string
  label: string
  sequenceNumber: number
  displayLabel: string
  status: string
  statusDisplay: string
  scheduledStart: string | null
  scheduledEnd: string | null
  actualStart: string | null
  actualEnd: string | null
  scheduledDate: string | null
  scheduledTime: string | null
  durationMinutes: number | null
  isPast: boolean
  isUpcoming: boolean
  isToday: boolean
  isOverdue: boolean
  isCompleted: boolean
  clientName: string
  beneficiaryName: string
  locationAddress: string | null
  locationCity: string | null
  locationGoogleMapsLink: string | null
  servicesCount: number
  completedServicesCount: number
  primaryServiceName: string | null
  primaryProviderName: string | null
  canStart: boolean
  canComplete: boolean
  canCancel: boolean
  canReschedule: boolean
  services: AppointmentService[]
  createdAt: string
}

export interface Quotation {
  id: string
  quotationNumber: string
  status: string
  taxAmount: string
  discountAmount: string
  totalAmount: string
  subtotal: string
  currency: string
  createdAt: string
  validUntil: string | null
}

export interface OrderDetails {
  id: string
  orderNumber: string
  status: string
  statusDisplay: string
  priority: string
  priorityDisplay: string
  createdAt: string
  updatedAt: string
  submittedAt: string | null
  confirmedAt: string | null
  completedAt: string | null
  cancelledAt: string | null
  
  // Client info
  clientName: string
  clientPhone: string
  clientEmail: string | null
  beneficiaryName: string
  beneficiaryPhone: string
  beneficiaryRelationship?: string | null
  /** Beneficiary onboarding survey answers as a JSON-encoded string (question code -> value). */
  beneficiaryProfile?: { surveyData: string | null } | null
  organizationName: string | null
  
  // Template/Category
  templateName: string | null
  categoryName: string | null
  
  // Pricing (client-facing: what the client pays)
  totalAmount: string
  subtotal: string
  vatAmount: string
  currency: string
  paymentStatus: string | null

  // Provider earnings (what the provider earns after platform commission)
  // Aggregated across all services in the order, scoped by viewer:
  // - ProfileProvider → only services assigned to them
  // - ProfileBusinessClient owning the order → all services
  // - ProfileOperation (active) → all services
  // Returns null when the viewer has no applicable audience or no providers are assigned.
  providerEarningsGrossAmount: string | null
  providerEarningsCommission: string | null
  providerEarningsCommissionRate: string | null
  providerEarningsNet: string | null
  providerEarningsTax: string | null
  providerEarningsTotal: string | null
  providerEarningsCurrency: string | null
  providerEarningsServicesCount: number | null
  
  // Services
  totalServices: number
  totalAppointments: number
  completedServicesCount: number
  pendingServicesCount: number
  inProgressServicesCount: number
  servicesProgressPercentage: number
  
  // Deadlines
  deadline: string | null
  daysUntilDeadline: number | null
  isOverdue: boolean
  
  // Capabilities
  canCancel: boolean
  canReschedule: boolean
  canConfirm: boolean
  canRefund: boolean
  
  // Location
  primaryLocationAddress: string | null
  primaryLocationCity: string | null
  primaryLocationGoogleMapsLink: string | null
  
  // Notes
  specialInstructions: string | null
  requirements: string | null
  
  // Related
  services: Service[]
  appointments: Appointment[]
  quotation: Quotation | null
}

// ============================================================================
// GraphQL Query
// ============================================================================

export const ORDER_DETAILS_QUERY = `
query OrderDetails($id: ID!) {
  order(id: $id) {
    id
    orderNumber
    status
    statusDisplay
    priority
    priorityDisplay
    createdAt
    updatedAt
    submittedAt
    confirmedAt
    completedAt
    cancelledAt
    clientName
    clientPhone
    clientEmail
    beneficiaryName
    beneficiaryPhone
    organizationName
    templateName
    categoryName
    totalAmount
    subtotal
    vatAmount
    currency
    paymentStatus
    providerEarningsGrossAmount
    providerEarningsCommission
    providerEarningsCommissionRate
    providerEarningsNet
    providerEarningsTax
    providerEarningsTotal
    providerEarningsCurrency
    providerEarningsServicesCount
    totalServices
    totalAppointments
    completedServicesCount
    pendingServicesCount
    inProgressServicesCount
    servicesProgressPercentage
    deadline
    daysUntilDeadline
    isOverdue
    canCancel
    canReschedule
    canConfirm
    canRefund
    primaryLocationAddress
    primaryLocationCity
    primaryLocationGoogleMapsLink
    specialInstructions
    requirements
    services {
      id
      status
      serviceName
      serviceDescription
      serviceCategory
      basePrice
      currency
      providerName
      providerPhone
      assignedProviderId
      appointmentStatus
      appointmentScheduledStart
      appointmentScheduledEnd
      locationAddress
      locationCity
      locationGoogleMapsLink
      canCancel
      completedAt
      createdAt
      clientFeedback
      providerFeedback
      hasClientFeedback
      hasProviderFeedback
      hasPendingSurvey
      pendingSurveyCount
      pendingSurveys {
        id
        surveyId
        surveyTitle
        isRequired
        isActive
        survey {
          id
          title
          description
          templateQuestions {
            id
            order
            question {
              id
              code
              questionType
              textAr
              textEn
              options {
                id
                value
                labelEn
                labelAr
                order
                score
              }
            }
          }
        }
      }
      serviceTemplate {
        id
        name
        description
        nameEn
        nameAr
        surveyAssignments {
          id
          surveyId
          surveyTitle
          isRequired
          isActive
          survey {
            id
            title
            description
            templateQuestions {
              id
              order
              question {
                id
                code
                questionType
                textAr
                textEn
                options {
                  id
                  value
                  labelEn
                  labelAr
                  order
                  score
                }
              }
            }
          }
        }
      }
      clientAnswersWithQuestions {
        questionText
        answer
        questionType
        questionCode
        rawAnswer
        instanceNumber
        instanceLabel
        scope
        templateQuestionId
        questionBankId
      }
      clientSurveyResponses {
        id
        submitted
        submittedAt
        answers
        completionPercentage
        surveySnapshot {
          id
          snapshotData
          createdAt
          survey {
            id
            title
            titleEn
            titleAr
          }
        }
        respondent {
          id
          firstName
          lastName
          phoneNumber
          providerProfile {
            fullName
            organizationName
            ratingDisplay
            totalReviews
            averageRating
          }
        }
      }
    }
    appointments {
      id
      label
      sequenceNumber
      displayLabel
      status
      statusDisplay
      scheduledStart
      scheduledEnd
      actualStart
      actualEnd
      scheduledDate
      scheduledTime
      durationMinutes
      isPast
      isUpcoming
      isToday
      isOverdue
      isCompleted
      clientName
      beneficiaryName
      locationAddress
      locationCity
      locationGoogleMapsLink
      servicesCount
      completedServicesCount
      primaryServiceName
      primaryProviderName
      canStart
      canComplete
      canCancel
      canReschedule
      createdAt
      services {
        id
        serviceName
        status
        providerName
        completedAt
      }
    }
    quotation {
      id
      quotationNumber
      status
      taxAmount
      discountAmount
      totalAmount
      subtotal
      currency
      createdAt
      validUntil
    }
    beneficiaryProfile {
      surveyData
    }
  }
}`

interface OrderDetailsResponse {
  order: OrderDetails
}

/**
 * Fetch comprehensive order details by ID
 */
export async function fetchOrderDetails(id: string): Promise<OrderDetails> {
  const response = await graphqlQuery<OrderDetailsResponse>(ORDER_DETAILS_QUERY, { id })
  if (!response.data?.order) {
    throw new Error('Order not found')
  }
  const order = response.data.order
  return {
    ...order,
    services: order.services.map((s) => ({
      ...s,
      isOverdue: computeServiceOverdue(s),
      clientAnswersWithQuestions: s.clientAnswersWithQuestions ?? [],
      pendingSurveys: s.pendingSurveys ?? [],
      clientSurveyResponses: s.clientSurveyResponses ?? [],
      serviceTemplate: s.serviceTemplate ?? null,
    })),
  }
}
